import { dupQwanta, persistQwantum } from 'qwanyx';
import { isAncestor } from '../utils/stackUtils';

export class StackOperations {
    constructor(debug = false, stackRootId = null) {
        this.debug = debug;
        this.stackRootId = stackRootId;
    }

    setStackRootId(stackRootId) {
        this.stackRootId = stackRootId;
    }

    async duplicateItem(itemId, cardDataMap) {
        if (!itemId) {
            throw new Error("No item ID provided for duplication");
        }

        const itemData = cardDataMap[itemId];
        if (!itemData) {
            throw new Error(`Item data not found for: ${itemId}`);
        }

        try {
            const result = await dupQwanta(itemId);

            if (result.success && result.newId) {
                if (this.debug) {
                    console.log(`Successfully duplicated item: ${itemId} -> ${result.newId}`);
                }
                return {
                    success: true,
                    newId: result.newId,
                    parentId: itemData.ed
                };
            } else {
                throw new Error(result.error || "Unknown duplication error");
            }
        } catch (error) {
            console.error("Error during duplication:", error);
            throw error;
        }
    }

    // FIXED: Modified createNewItem to accept custom parentId parameter
    async createNewItem(sourceId, title = null, parentId = null) {
        if (!sourceId) {
            throw new Error("Source ID is required for new item creation");
        }

        // Use provided parentId, or fall back to stackRootId
        const targetParentId = parentId || this.stackRootId;
        
        if (!targetParentId) {
            throw new Error("Parent ID is required for new item creation");
        }

        try {
            const overrideProps = { ed: targetParentId };

            if (title && title.trim()) {
                overrideProps.title = title.trim();
            }

            if (this.debug) {
                console.log("Creating new item with properties:", {
                    sourceId,
                    parentId: targetParentId,
                    overrideProps
                });
            }

            const result = await dupQwanta(sourceId, null, overrideProps, false, title);

            if (result.success && result.newId) {
                if (this.debug) {
                    console.log(`Successfully created new item from template: ${sourceId} -> ${result.newId} with parent: ${targetParentId}`);
                    if (title) {
                        console.log(`Assigned title: "${title}"`);
                    }
                }
                return {
                    success: true,
                    newId: result.newId,
                    parentId: targetParentId
                };
            } else {
                throw new Error(result.error || "Unknown creation error");
            }
        } catch (error) {
            console.error("Error during new item creation:", error);
            throw error;
        }
    }

    async moveItem(draggedItemId, targetId, sourceParentId) {
        // Validation checks
        if (draggedItemId === targetId) {
            throw new Error("Cannot move item: Item cannot be dropped on itself");
        }

        // Check for circular reference
        const willCreateCircularReference = await isAncestor(draggedItemId, targetId);
        if (willCreateCircularReference) {
            throw new Error("Cannot move item: This would create a circular reference");
        }

        try {
            // Update the dragged item's parent
            await persistQwantum(draggedItemId, "ed", targetId, false);

            if (this.debug) {
                console.log(`Successfully moved item ${draggedItemId} from ${sourceParentId} to ${targetId}`);
            }

            return {
                success: true,
                draggedItemId,
                targetId,
                sourceParentId
            };
        } catch (error) {
            console.error("Failed to move item:", error);
            throw new Error(`Error moving item: ${error.message}`);
        }
    }

    async updateItemTitle(itemId, newTitle) {
        if (!itemId || !newTitle || !newTitle.trim()) {
            throw new Error("Item ID and title are required");
        }

        try {
            await persistQwantum(itemId, "title", newTitle.trim(), false);
            
            if (this.debug) {
                console.log("Title updated in database:", newTitle);
            }

            return { success: true };
        } catch (error) {
            console.error("Failed to update title in database:", error);
            throw error;
        }
    }

    async makeItemFolder(itemId) {
        try {
            await persistQwantum(itemId, "isFolder", true, false);
            
            if (this.debug) {
                console.log(`Made item ${itemId} a folder`);
            }

            return { success: true };
        } catch (error) {
            console.error("Failed to make item a folder:", error);
            throw error;
        }
    }

    async updateViewPreference(itemId, viewId) {
        try {
            await persistQwantum(itemId, "vi", viewId);
            
            if (this.debug) {
                console.log(`Saved view preference "${viewId}" for item ${itemId}`);
            }

            return { success: true };
        } catch (error) {
            console.error("Error saving view preference:", error);
            throw error;
        }
    }
}